import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { usePasswordChange, usePasswordValidation } from '../hooks';
import { PLACEHOLDERS, BUTTON_LABELS, PASSWORD_ERROR_MESSAGES } from '../constants';

/**
 * SecurityCard - Card de segurança
 *
 * Formulário para alteração de senha com validação em tempo real
 * Migrado para Tailwind CSS v4
 */
export const SecurityCard = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { changePassword, loading, error, success, clearMessages } = usePasswordChange();
  const { errors, isValid, confirmMatches } = usePasswordValidation(newPassword, confirmPassword);

  const canSubmit = newPassword.length > 0 && confirmPassword.length > 0 && isValid && confirmMatches && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) return;

    try {
      await changePassword(newPassword);
      // Clear form on success
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);
    clearMessages();
  };

  const handleConfirmChange = (value: string) => {
    setConfirmPassword(value);
    clearMessages();
  };

  return (
    <div className="bg-[#2a2a2a] rounded-xl p-6 border border-[#333333] hover:border-[#F4CC04] transition-all duration-400">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#F4CC04]/10 flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-[#F4CC04]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div className="text-2xl font-bold text-white m-0">Segurança</div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-3 bg-[#10b981]/10 border border-[#10b981] rounded-lg">
          <div className="text-sm text-[#10b981] font-medium m-0">{success}</div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-[#ef4444]/10 border border-[#ef4444] rounded-lg">
          <div className="text-sm text-[#ef4444] font-medium m-0">{error}</div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Password Fields Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* New Password */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-[#999999] uppercase tracking-wide">
              Nova Senha
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => handlePasswordChange(e.target.value)}
                placeholder={PLACEHOLDERS.newPassword}
                autoComplete="new-password"
                className="w-full px-4 py-3 pr-11 bg-[#1a1a1a] border border-[#333333] rounded-lg
                  text-white font-medium text-sm
                  placeholder:text-[#666666] placeholder:text-sm
                  focus:outline-none focus:border-[#F4CC04] focus:ring-1 focus:ring-[#F4CC04]
                  transition-all duration-300"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#CCCCCC] transition-colors"
                aria-label={showNew ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showNew ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-[#999999] uppercase tracking-wide">
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => handleConfirmChange(e.target.value)}
                placeholder={PLACEHOLDERS.confirmPassword}
                autoComplete="new-password"
                className="w-full px-4 py-3 pr-11 bg-[#1a1a1a] border border-[#333333] rounded-lg
                  text-white font-medium text-sm
                  placeholder:text-[#666666] placeholder:text-sm
                  focus:outline-none focus:border-[#F4CC04] focus:ring-1 focus:ring-[#F4CC04]
                  transition-all duration-300"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#CCCCCC] transition-colors"
                aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showConfirm ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Validation Errors */}
        {newPassword.length > 0 && errors.length > 0 && (
          <ul className="space-y-1 px-1">
            {errors.map((err) => (
              <li key={err} className="text-xs text-[#ef4444] flex items-start gap-1.5">
                <span className="text-[#ef4444] mt-0.5">•</span>
                <span>{err}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Confirm Match Error */}
        {confirmPassword.length > 0 && !confirmMatches && (
          <div className="text-xs text-[#ef4444] flex items-start gap-1.5 px-1 m-0">
            <span className="text-[#ef4444] mt-0.5">•</span>
            <span>{PASSWORD_ERROR_MESSAGES.mismatch}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full mt-4 px-6 py-3.5 bg-[#F4CC04] text-[#1a1a1a] font-bold rounded-lg text-base
            hover:bg-[#f5d633] active:scale-[0.98]
            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#F4CC04]
            transition-all duration-300
            flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Atualizando...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{BUTTON_LABELS.updatePassword}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};
